/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · Sprint 3 · Business day, shifts, reports, KDS, tokens.
 *
 * All state is read/dispatched via the parent's reducer — no fetch, no
 * persistence, no feature-gate wiring. The whole surface resets on
 * refresh, per the strict mock-only Sprint 3 scope.
 *
 * Every module here derives its data from state.orders + state.session.
 * The Z report snapshots the shift's numbers as a payload so the
 * printed artefact is stable even after subsequent edits (Contract §3
 * fiscal immutability rule).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { CalendarDays, Sun, Sunset, ClipboardList, Printer, X as XIcon,
  Utensils, GlassWater, AlertTriangle, ChevronRight, TrendingUp, ReceiptText,
  Clock, Tag, DollarSign, ListChecks, Layers, Coffee, Ban } from "lucide-react";
import { ACT, ORDER_STATE, ORDER_TYPE, computeTotalsFor, tableState, staffNameFor } from "./posReducer";
import { ITEMS, CATEGORIES, TABLES, TAX_CONFIG } from "./mockData";
// Item 2 (Sprint 4 corrections) · resolve internal table_id → display
// label at every surface.
const tblLabel = (id) => TABLES.find((t) => t.id === id)?.label || id;
import { POS_CONFIG } from "./posConfig";

const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ─── Selectors on state for Sprint 3 ────────────────────────────────
export const isBusinessDayOpen = (state) =>
  !!state.session.business_date && !state.session.business_day_closed_at;

export const openShifts = (state) =>
  (state.session.shifts || []).filter((s) => !s.closed_at);

export const closedShifts = (state) =>
  (state.session.shifts || []).filter((s) => s.closed_at);

// Aggregate settlement totals + counts across every SETTLED order.
// Used by X/Z reports, cash reconciliation, and the settlement summary.
export const aggregateSettlements = (state, filter = () => true) => {
  const by_mode = {};
  let bills_issued = 0, bills_voided = 0, invoices_voided = 0;
  const orders = Object.values(state.orders);
  for (const o of orders) {
    if (!filter(o)) continue;
    if (o.order.state === ORDER_STATE.SETTLED) {
      bills_issued += 1;
      for (const p of o.payments || []) {
        if (p.status === "pending") continue;
        if (!by_mode[p.mode]) by_mode[p.mode] = { total: 0, count: 0, refs: [] };
        by_mode[p.mode].total += Number(p.amount || 0);
        by_mode[p.mode].count += 1;
        if (p.reference) by_mode[p.mode].refs.push(p.reference);
      }
    }
    if (o.order.state === ORDER_STATE.VOIDED) bills_voided += 1;
  }
  invoices_voided = (state.session.invoice_voids || []).length;
  return { by_mode, bills_issued, bills_voided, invoices_voided };
};

export const aggregateSales = (state, filter = () => true) => {
  const orders = Object.values(state.orders).filter(filter);
  let gross = 0, discount = 0, net = 0, tax_cgst = 0, tax_sgst = 0, tax_vat = 0;
  let covers = 0;
  const by_item = {};
  const by_hour = {};
  const discounts_by_reason = {};
  const opens = [];
  let first_bill = null, last_bill = null;
  for (const o of orders) {
    if (o.order.state !== ORDER_STATE.SETTLED) continue;
    const t = computeTotalsFor(state, o.order.id);
    gross    += t.subtotal;
    discount += t.discount;
    net      += t.net_subtotal;
    tax_cgst += t.cgst;
    tax_sgst += t.sgst;
    tax_vat  += t.vat;
    covers   += Number(o.order.covers || 0);
    if (o.order.invoice_no) {
      if (!first_bill || o.order.invoice_no < first_bill.invoice_no)
        first_bill = { invoice_no: o.order.invoice_no, at: o.order.opened_at };
      if (!last_bill  || o.order.invoice_no > last_bill.invoice_no)
        last_bill  = { invoice_no: o.order.invoice_no, at: o.order.opened_at };
    }
    for (const l of o.lines || []) {
      const key = l.item_id;
      if (!by_item[key]) by_item[key] = { name: l.item_name, qty: 0, value: 0, category: null, open_item: !!l.is_open_item, punched_by: l.punched_by || null };
      by_item[key].qty   += Number(l.qty || 0);
      by_item[key].value += Number(l.qty || 0) * Number(l.unit_price || 0) - Number(l.discount || 0);
      const it = ITEMS.find((x) => x.id === l.item_id);
      by_item[key].category = it?.category_id || (l.open_item_category === "alcohol" ? "cat_liquor" : "cat_open");
      if (l.is_open_item) opens.push({ line: l, order: o.order });
    }
    const hour = new Date(o.order.opened_at).getHours();
    by_hour[hour] = by_hour[hour] || { covers: 0, sales: 0, bills: 0 };
    by_hour[hour].bills  += 1;
    by_hour[hour].sales  += t.grand_total;
    by_hour[hour].covers += Number(o.order.covers || 0);
    if (o.order.discount) {
      const key = o.order.discount.reason || "MANAGER_DISCRETION";
      if (!discounts_by_reason[key]) discounts_by_reason[key] = { amount: 0, count: 0 };
      discounts_by_reason[key].amount += t.discount;
      discounts_by_reason[key].count  += 1;
    }
  }
  return { gross, discount, net, tax_cgst, tax_sgst, tax_vat,
           tax_total: tax_cgst + tax_sgst + tax_vat, covers,
           by_item, by_hour, discounts_by_reason, opens,
           first_bill, last_bill };
};

// Compute cash-drawer expectations for a shift: opening float + all
// cash tenders minus refunds/payouts (prototype has no refunds).
export const computeExpectedCash = (state, opening_float) => {
  const { by_mode } = aggregateSettlements(state);
  const cash_tender = by_mode.cash?.total || 0;
  return Number(opening_float || 0) + cash_tender;
};

// ═══════════════════════════════════════════════════════════════════
// Part 6 · Business day banner + open/close dialogs
// ═══════════════════════════════════════════════════════════════════
export function BusinessDayBanner({ state }) {
  const open = isBusinessDayOpen(state);
  return (
    <div className={`h-8 px-3 flex items-center gap-2 text-[11px] shrink-0
      ${open ? "bg-emerald-50 border-b border-emerald-200 text-emerald-900"
             : "bg-amber-100 border-b border-amber-300 text-amber-900"}`}
      data-testid="pos-business-day-banner">
      <CalendarDays className="w-3.5 h-3.5" />
      {open
        ? <span data-testid="pos-business-day-open">
            Business day <strong className="tabular-nums">{state.session.business_date}</strong> open · opened by <strong>{state.session.business_day_opened_by}</strong>
          </span>
        : <span data-testid="pos-business-day-closed">
            No business day open. Nothing can be ordered until a day is opened.
          </span>
      }
    </div>
  );
}

export function BusinessDayOpenDialog({ open, onCancel, onConfirm, lastClosedDate }) {
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  // POS Lifecycle Final Fix · Default is now `lastClosedDate + 1` when
  // present, otherwise today. This prevents the silent collision where
  // the manager taps "Open day" on a date the outlet just closed and
  // gets a duplicate-key 500 from the server (now a friendly 409, but
  // the picker should never suggest a used date in the first place).
  const nextAfterLast = useMemo(() => {
    if (!lastClosedDate) return null;
    const [y, m, d] = lastClosedDate.split("-").map((n) => Number(n));
    const dt = new Date(Date.UTC(y, m - 1, d + 1));
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  }, [lastClosedDate]);
  const defaultDate = nextAfterLast && nextAfterLast > today ? nextAfterLast
                     : nextAfterLast || today;
  const [date, setDate] = useState(defaultDate);
  useEffect(() => { if (open) setDate(defaultDate); }, [open, defaultDate]);
  const minDate = nextAfterLast;
  const isReopen = !!(lastClosedDate && date <= lastClosedDate);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[460px] p-0 gap-0 border border-emerald-200 overflow-hidden"
        data-testid="pos-business-day-open-dialog"
        data-reopen={isReopen ? "1" : "0"}>
        <DialogHeader className="px-5 py-3 border-b border-emerald-100 bg-emerald-50 space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] flex items-center gap-1.5">
            <Sun className="w-4 h-4 text-emerald-700" /> Open business day
          </DialogTitle>
          <DialogDescription className="text-[11px] text-emerald-900">
            The business date is an explicit service period, not a calendar date.
            A bar closing at 1:30am can post to the previous business date.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Business date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              min={minDate || undefined}
              className={`w-full h-11 rounded border px-3 text-[13px] tabular-nums focus:outline-none
                ${isReopen ? "border-rose-400 bg-rose-50 focus:border-rose-500"
                           : "border-[color:var(--pos-color-border,#E2E8DF)] focus:border-emerald-500"}`}
              data-testid="pos-business-day-date" />
          </div>
          {lastClosedDate && (
            <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic" data-testid="pos-business-day-last-closed">
              Last closed business day: <strong className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">{lastClosedDate}</strong>. Any earlier date needs an admin override.
            </div>
          )}
          {isReopen && (
            <div className="rounded border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-900"
              data-testid="pos-business-day-reopen-warn">
              This is a REOPEN of a closed day. Confirming will prompt for the manager PIN and a reason,
              and the action will appear in the Exception Report.
            </div>
          )}
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-business-day-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button"
            onClick={() => onConfirm({ business_date: date, is_reopen: isReopen })}
            data-testid="pos-business-day-confirm"
            className={`h-10 px-4 rounded text-[12px] font-semibold text-white
              ${isReopen ? "bg-rose-700 hover:bg-rose-800" : "bg-emerald-700 hover:bg-emerald-800"}`}>
            {isReopen ? "Continue to override" : "Open day"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BusinessDayCloseDialog({ open, state, livePos, onCancel, onConfirm, onJumpToOrder }) {
  // POS Cleanup Pass 3 · The blocker list now comes from the
  // canonical server endpoint (`GET /pos/orders/unresolved`) — the
  // same predicate the backend uses to refuse the actual close. This
  // eliminates the drift where the UI showed "all clear" while
  // /pos/business-days/close returned "33 unsettled" (stale zero-line
  // OPEN drafts that the frontend was silently filtering out).
  const [remote, setRemote] = useState({ loading: false, orders: null, error: null });
  useEffect(() => {
    if (!open) return;
    if (!livePos) { setRemote({ loading: false, orders: [], error: null }); return; }
    let cancelled = false;
    setRemote({ loading: true, orders: null, error: null });
    (async () => {
      try {
        const data = await livePos.fetchUnresolvedOrders();
        if (cancelled) return;
        setRemote({ loading: false, orders: data?.orders || [], error: null });
      } catch (e) {
        if (cancelled) return;
        setRemote({ loading: false, orders: [], error: e?.message || "Could not load unresolved orders" });
      }
    })();
    return () => { cancelled = true; };
  }, [open, livePos]);

  const unsettled = remote.orders || [];
  const blocked = unsettled.length > 0 || remote.loading;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[640px] max-h-[90vh] p-0 gap-0 border border-amber-200 overflow-hidden flex flex-col"
        data-testid="pos-business-day-close-dialog">
        <DialogHeader className="px-5 py-3 border-b border-amber-100 bg-amber-50 space-y-0 shrink-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] flex items-center gap-1.5">
            <Sunset className="w-4 h-4 text-amber-700" /> Close business day
          </DialogTitle>
          <DialogDescription className="text-[11px] text-amber-900">
            Every unresolved order must be settled or voided before the day can close. Open tills
            and the internal shift are walked through and closed as part of this flow.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {remote.loading && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700"
              data-testid="pos-business-day-close-loading">
              Checking unresolved orders on the server…
            </div>
          )}
          {!remote.loading && !blocked && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-900"
              data-testid="pos-business-day-close-ready">
              All clear. The day <strong className="tabular-nums">{state.session.business_date}</strong> can close.
            </div>
          )}
          {!remote.loading && unsettled.length > 0 && (
            <div className="space-y-1" data-testid="pos-business-day-blockers-orders">
              <div className="text-[10px] uppercase tracking-wider text-amber-900 font-semibold">
                {unsettled.length} unresolved order{unsettled.length === 1 ? "" : "s"}
              </div>
              {unsettled.map((o) => (
                <button key={o.order_id} type="button"
                  onClick={() => onJumpToOrder && onJumpToOrder(o.order_id)}
                  data-testid={`pos-blocker-order-${o.order_id}`}
                  className="w-full text-left px-3 py-2 rounded border border-amber-200 bg-white hover:bg-amber-50 text-[11px] flex items-start justify-between gap-2">
                  <span className="flex-1 min-w-0">
                    <span className="block">
                      <strong className="tabular-nums">{tblLabel(o.table_id) || o.token || o.invoice_no || (o.order_id || "").slice(0, 8)}</strong>
                      {" · "}{(o.type || "").replace("_", " ") || "order"}{" · "}<span className="uppercase">{o.state}</span>
                      {o.gross_total ? <span className="ml-1 tabular-nums text-amber-900">· ₹{Number(o.gross_total).toFixed(2)}</span> : null}
                    </span>
                    <span className="block text-[10px] text-amber-800 italic mt-0.5">{o.block_reason}</span>
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={onCancel}
            data-testid="pos-business-day-close-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button" onClick={() => onConfirm()}
            disabled={blocked}
            data-testid="pos-business-day-close-confirm"
            className="h-10 px-4 rounded bg-amber-700 text-white text-[12px] font-semibold hover:bg-amber-800 disabled:opacity-40 disabled:cursor-not-allowed">
            Close day
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 7 · Shifts are now INTERNAL only.
//   POS Cleanup Pass 3 · The former `ShiftOpenDialog` and
//   `ShiftCloseDialog` (denomination grid, EDC batch, blind close,
//   variance reasons) have been removed from the operator-facing
//   surface. Shifts still exist server-side (see /pos/shifts/open and
//   /pos/shifts/close) — they auto-open on the first staff PIN login
//   of a business day and auto-close during Close Day. Cash / EDC
//   reconciliation lives on the Till Close flow
//   (see TillShiftCloseFlow.js).
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// Part 8 · X and Z reports
// ═══════════════════════════════════════════════════════════════════
function ReportShell({ title, subtitle, children, onClose, testid }) {
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[820px] w-[92vw] max-h-[92vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid={testid}>
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] shrink-0 space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">{title}</DialogTitle>
          <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">{subtitle}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5 text-[12px] leading-relaxed">
          {children}
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" disabled className="h-9 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] text-[#B8B8B5] cursor-not-allowed"
            title="Export is UI-only in this sprint" data-testid={`${testid}-export`}>
            Export
          </button>
          <button type="button" disabled className="h-9 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] text-[#B8B8B5] cursor-not-allowed inline-flex items-center gap-1"
            data-testid={`${testid}-print`}>
            <Printer className="w-3 h-3" /> Print
          </button>
          <button type="button" onClick={onClose}
            data-testid={`${testid}-close`}
            className="h-9 px-3 rounded bg-[color:var(--color-primary)] text-white text-[11px] font-semibold hover:bg-[color:var(--color-primary-hover)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportRow({ label, value, testid, bold }) {
  return (
    <div className={`flex items-baseline justify-between py-0.5 ${bold ? "font-semibold border-t border-[color:var(--pos-color-border,#E2E8DF)] mt-1 pt-1" : ""}`}>
      <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{label}</span>
      <span className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid={testid}>{value}</span>
    </div>
  );
}

function ReportSection({ title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold border-b border-[color:var(--pos-color-border,#E2E8DF)] pb-1 mb-1.5">{title}</div>
      {children}
    </div>
  );
}

// Render the X or Z report body — same shape, different provenance.
function XZReportBody({ state, snapshot, kind }) {
  const sales   = snapshot.sales;
  const stl     = snapshot.settlements;
  const void_disc_audit = snapshot.audit || {};
  const shift   = snapshot.shift || null;

  return (
    <>
      <ReportSection title="Header">
        <ReportRow label="Business date" value={<span className="tabular-nums">{state.session.business_date}</span>} testid="rpt-business-date" />
        {/* Sprint 2 Consolidation Part 7 · full outlet NAME, never
            the short outlet_code (`WE`, `RB`). Snapshot captures the
            name at report-take time so historical Z reports keep the
            outlet they were taken at even if the tenant is renamed. */}
        <ReportRow label="Outlet" value={snapshot.outlet_name || state.session.outlet_name || state.session.outlet_code} testid="rpt-outlet" />
        {kind === "Z" && (
          <ReportRow label="Z number" value={<span className="tabular-nums">{snapshot.z_number}</span>} testid="rpt-z-number" />
        )}
        {/* Sprint 2 Consolidation Part 7 · actor is the RESOLVED NAME
            of the staff member who took the report. "system" is a
            placeholder and must never render on the printed report. */}
        <ReportRow label="Taken by" value={snapshot.taken_by && snapshot.taken_by !== "system"
            ? snapshot.taken_by
            : (snapshot.generated_by && snapshot.generated_by !== "system"
                ? snapshot.generated_by
                : "—")} testid="rpt-taken-by" />
        <ReportRow label="Taken at" value={<span className="tabular-nums">{new Date(snapshot.at).toLocaleString("en-IN")}</span>} />
      </ReportSection>

      <ReportSection title="Sales">
        <ReportRow label="Gross sales"  value={`₹${fmt2(sales.gross)}`}    testid="rpt-gross"    />
        <ReportRow label="Discounts"    value={`− ₹${fmt2(sales.discount)}`} testid="rpt-discount" />
        <ReportRow label="Net sales"    value={`₹${fmt2(sales.net)}`}      testid="rpt-net" bold />
      </ReportSection>

      <ReportSection title="Tax breakup">
        <ReportRow label="CGST @ 2.5%"  value={`₹${fmt2(sales.tax_cgst)}`} testid="rpt-cgst" />
        <ReportRow label="SGST @ 2.5%"  value={`₹${fmt2(sales.tax_sgst)}`} testid="rpt-sgst" />
        <ReportRow label="VAT @ 20% (Liquor)" value={`₹${fmt2(sales.tax_vat)}`} testid="rpt-vat" />
        <ReportRow label="Tax total"    value={`₹${fmt2(sales.tax_total)}`} bold />
      </ReportSection>

      <ReportSection title="Settlement by mode">
        {Object.entries(stl.by_mode).length === 0
          ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No settlements yet.</div>
          : Object.entries(stl.by_mode).map(([mode, row]) => (
            <ReportRow key={mode} label={<span className="tabular-nums">{mode.replace(/_/g, " ")} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{row.count}</span></span>}
              value={`₹${fmt2(row.total)}`} testid={`rpt-mode-${mode}`} />
          ))
        }
      </ReportSection>

      <ReportSection title="Cash reconciliation">
        <ReportRow label="Opening float" value={`₹${fmt2(shift?.opening_float || 0)}`} testid="rpt-float" />
        <ReportRow label="Cash sales"    value={`₹${fmt2(stl.by_mode.cash?.total || 0)}`} testid="rpt-cash-sales" />
        <ReportRow label="Expected cash" value={`₹${fmt2((shift?.opening_float || 0) + (stl.by_mode.cash?.total || 0))}`} testid="rpt-cash-expected" bold />
        {kind === "Z" && shift?.counted_cash != null && (
          <>
            <ReportRow label="Counted cash" value={`₹${fmt2(shift.counted_cash)}`} testid="rpt-cash-counted" />
            <ReportRow label="Variance"     value={`${shift.variance >= 0 ? "+" : ""}₹${fmt2(shift.variance)}`} testid="rpt-cash-variance" />
          </>
        )}
      </ReportSection>

      <ReportSection title="Covers">
        <ReportRow label="Total covers" value={<span className="tabular-nums">{sales.covers}</span>} testid="rpt-covers" />
        <ReportRow label="Bills issued" value={<span className="tabular-nums">{stl.bills_issued}</span>} testid="rpt-bills" />
        <ReportRow label="Bills voided" value={<span className="tabular-nums">{stl.bills_voided}</span>} testid="rpt-bills-voided" />
        <ReportRow label="Invoices voided" value={<span className="tabular-nums">{stl.invoices_voided}</span>} testid="rpt-invoices-voided" />
        <ReportRow label="Avg / cover"  value={`₹${fmt2(sales.covers ? sales.net / sales.covers : 0)}`} />
        <ReportRow label="Avg / bill"   value={`₹${fmt2(stl.bills_issued ? sales.net / stl.bills_issued : 0)}`} />
      </ReportSection>

      <ReportSection title="Discounts by reason">
        {Object.entries(sales.discounts_by_reason || {}).length === 0
          ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No discounts.</div>
          : Object.entries(sales.discounts_by_reason).map(([r, row]) => (
            <ReportRow key={r} label={`${r} · ${row.count}`} value={`₹${fmt2(row.amount)}`} testid={`rpt-disc-${r}`} />
          ))
        }
      </ReportSection>

      <ReportSection title="Voids and cancellations">
        {(void_disc_audit.voids || []).length === 0 && (void_disc_audit.cancellations || []).length === 0
          ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">Nothing to report.</div>
          : (
            <>
              {(void_disc_audit.voids || []).map((v, i) => (
                <div key={`v${i}`} className="text-[11px] py-0.5 flex items-center justify-between">
                  <span>Void · <span className="tabular-nums">{v.voided_invoice || "—"}</span> · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{v.reason_code}</span></span>
                  <span className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">by {v.authorised_by}</span>
                </div>
              ))}
              {(void_disc_audit.cancellations || []).map((c, i) => (
                <div key={`c${i}`} className="text-[11px] py-0.5 flex items-center justify-between">
                  <span>Cancel · <span className="tabular-nums">{c.ticket_id}</span> · {c.item_name} × {c.qty_cancelled} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{c.reason_code}</span></span>
                  <span className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">by {c.authorised_by}</span>
                </div>
              ))}
            </>
          )
        }
      </ReportSection>

      <ReportSection title="Open item sales">
        {(sales.opens || []).length === 0
          ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No open items punched.</div>
          : (sales.opens || []).map((o, i) => (
            <div key={i} className="text-[11px] py-0.5 flex items-center justify-between">
              <span className="tabular-nums">{o.line.item_name} × {o.line.qty}</span>
              <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">by {o.line.punched_by}</span>
            </div>
          ))
        }
      </ReportSection>

      <ReportSection title="Bill number range">
        <ReportRow label="First bill" value={sales.first_bill ? <span className="tabular-nums">{sales.first_bill.invoice_no} · {new Date(sales.first_bill.at).toLocaleTimeString()}</span> : "—"} testid="rpt-first-bill" />
        <ReportRow label="Last bill"  value={sales.last_bill  ? <span className="tabular-nums">{sales.last_bill.invoice_no}  · {new Date(sales.last_bill.at).toLocaleTimeString()}</span>  : "—"} testid="rpt-last-bill" />
      </ReportSection>
    </>
  );
}

export function XReportDialog({ state, outletName, onClose }) {
  // Sprint 2 Consolidation Part 7 · actor + outlet-name resolution.
  // Prefer the currently-signed-in staff snapshot's name; fall back to
  // a resolved lookup from the reducer's staff directory; never emit
  // the "system" placeholder.
  const actor = state?.session?.staff_snapshot?.name
    || (state?.session?.logged_in_staff_id
          ? (staffNameFor(state, state.session.logged_in_staff_id) || null)
          : null);
  const snapshot = useMemo(() => ({
    at: Date.now(),
    taken_by: actor,
    outlet_name: outletName || state.session.outlet_name || null,
    sales: aggregateSales(state),
    settlements: aggregateSettlements(state),
    audit: {
      voids:         state.session.invoice_voids || [],
      cancellations: state.session.ticket_cancellations || [],
    },
    shift: openShifts(state)[0] || closedShifts(state).slice(-1)[0] || null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, actor, outletName]);
  return (
    <ReportShell title="X report · mid-shift read"
      subtitle="Non-destructive. Repeatable. Closes nothing."
      onClose={onClose} testid="pos-x-report">
      <XZReportBody state={state} snapshot={snapshot} kind="X" />
    </ReportShell>
  );
}

export function ZReportDialog({ state, zEntry, onClose }) {
  const snapshot = zEntry?.snapshot || {
    at: Date.now(),
    generated_by: zEntry?.generated_by || "system",
    z_number: zEntry?.z_number,
    sales: aggregateSales(state),
    settlements: aggregateSettlements(state),
    audit: {
      voids:         state.session.invoice_voids || [],
      cancellations: state.session.ticket_cancellations || [],
    },
    shift: closedShifts(state).slice(-1)[0] || openShifts(state)[0] || null,
  };
  return (
    <ReportShell title={`Z report · ${snapshot.z_number || "—"}`}
      subtitle="Fiscal artifact · immutable once generated · reprintable"
      onClose={onClose} testid="pos-z-report">
      <XZReportBody state={state} snapshot={{ ...snapshot, z_number: snapshot.z_number }} kind="Z" />
    </ReportShell>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 9 · Operational reports
// ═══════════════════════════════════════════════════════════════════
export function ReportsHub({ state, onClose, onOpenReport }) {
  const reports = [
    { id: "exception",        label: "Exception report",      icon: AlertTriangle, desc: "Loss, wastage, fraud-risk — read this first." },
    { id: "bill_register",    label: "Bill register",         icon: ReceiptText,   desc: "Every bill, voided invoices marked." },
    { id: "void_discount",    label: "Void & discount audit", icon: AlertTriangle, desc: "Shrinkage report — read this first." },
    { id: "item_sales",       label: "Item sales",            icon: ListChecks,    desc: "Quantity + value by item, grouped by category." },
    { id: "hourly_sales",     label: "Hourly sales",          icon: Clock,         desc: "Sales and covers by hour." },
    { id: "tax_summary",      label: "Tax summary",           icon: Tag,           desc: "Taxable value + tax by rate and regime." },
    { id: "settlement_summary", label: "Settlement summary",  icon: DollarSign,    desc: "By mode with references listed." },
  ];
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[720px] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden"
        data-testid="pos-reports-hub">
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Reports</DialogTitle>
          <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            Read from live reducer state. All render through the same preview shell so they print on the same stationery.
          </DialogDescription>
        </DialogHeader>
        <div className="p-3 grid grid-cols-2 gap-2">
          {reports.map((r) => (
            <button key={r.id} type="button"
              onClick={() => onOpenReport(r.id)}
              data-testid={`pos-reports-hub-${r.id}`}
              className="text-left rounded border border-[color:var(--pos-color-border,#E2E8DF)] p-3 hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition">
              <div className="flex items-center gap-2 mb-1">
                <r.icon className="w-3.5 h-3.5 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />
                <span className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">{r.label}</span>
              </div>
              <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] leading-snug">{r.desc}</div>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end">
          <button type="button" onClick={onClose}
            data-testid="pos-reports-hub-close"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Bill register — every SETTLED and VOIDED bill.
export function BillRegisterReport({ state, onClose }) {
  const rows = useMemo(() => Object.values(state.orders)
    .filter((o) => o.order.state === ORDER_STATE.SETTLED || o.order.state === ORDER_STATE.VOIDED)
    .sort((a, b) => (a.order.invoice_no || "").localeCompare(b.order.invoice_no || "")),
    [state.orders]);
  return (
    <ReportShell title="Bill register" subtitle="Every settled + voided bill. Voided invoices shown and marked."
      onClose={onClose} testid="pos-report-bill-register">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            <th className="text-left py-1.5 pr-2">Invoice</th>
            <th className="text-left py-1.5 pr-2">Time</th>
            <th className="text-left py-1.5 pr-2">Type</th>
            <th className="text-left py-1.5 pr-2">Table/Token</th>
            <th className="text-right py-1.5 pr-2">Cov</th>
            <th className="text-right py-1.5 pr-2">Gross</th>
            <th className="text-right py-1.5 pr-2">Disc</th>
            <th className="text-right py-1.5 pr-2">Tax</th>
            <th className="text-right py-1.5 pr-2">Net</th>
            <th className="text-left py-1.5 pr-2">Mode</th>
            <th className="text-left py-1.5">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={11} className="py-6 text-center italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No bills yet.</td></tr>
          )}
          {rows.map((o) => {
            const t = computeTotalsFor(state, o.order.id);
            const modes = (o.payments || []).map((p) => p.mode).join(", ");
            const voided = o.order.state === ORDER_STATE.VOIDED;
            return (
              <tr key={o.order.id} className={`border-b border-[#EEF0EC] ${voided ? "bg-rose-50/60" : ""}`}
                data-testid={`pos-bill-register-row-${o.order.invoice_no || o.order.id}`}>
                <td className="py-1 pr-2 tabular-nums">
                  {o.order.invoice_no || "—"}
                  {voided && <span className="ml-1 text-[9px] uppercase text-rose-800 bg-rose-100 border border-rose-200 rounded px-1">VOID</span>}
                </td>
                <td className="py-1 pr-2 tabular-nums">{new Date(o.order.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                <td className="py-1 pr-2 uppercase text-[10px]">{o.order.type.replace("_", " ")}</td>
                <td className="py-1 pr-2 tabular-nums">{tblLabel(o.order.table_id) || o.order.token || "—"}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{o.order.covers || 0}</td>
                <td className="py-1 pr-2 text-right tabular-nums">₹{fmt2(t.subtotal)}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-rose-700">−₹{fmt2(t.discount)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">₹{fmt2(t.tax_total)}</td>
                <td className="py-1 pr-2 text-right tabular-nums font-medium">₹{fmt2(t.grand_total)}</td>
                <td className="py-1 pr-2 text-[10px] uppercase">{modes || "—"}</td>
                <td className="py-1 text-[10px] uppercase">{o.order.state}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ReportShell>
  );
}

export function VoidDiscountAuditReport({ state, onClose }) {
  const voids   = state.session.invoice_voids || [];
  const cancels = state.session.ticket_cancellations || [];
  const discountedOrders = Object.values(state.orders).filter((o) => o.order.discount);
  const voidedOrders = Object.values(state.orders).filter((o) => o.order.state === ORDER_STATE.VOIDED);
  return (
    <ReportShell title="Void & discount audit"
      subtitle="Shrinkage report · every void, invoice void, discount and ticket cancellation."
      onClose={onClose} testid="pos-report-void-discount">
      <ReportSection title="Voided orders">
        {voidedOrders.length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">None.</div>}
        {voidedOrders.map((o) => {
          const t = computeTotalsFor(state, o.order.id);
          return (
            <div key={o.order.id} className="flex items-center justify-between py-0.5" data-testid={`pos-void-audit-order-${o.order.id}`}>
              <span className="tabular-nums">{o.order.invoice_no || o.order.id} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{o.order.void_reason}</span></span>
              <span className="tabular-nums">₹{fmt2(t.grand_total)} · by {o.order.void_authorised_by} · {new Date(o.order.void_at || 0).toLocaleTimeString()}</span>
            </div>
          );
        })}
      </ReportSection>
      <ReportSection title="Voided invoices (post-print splits)">
        {voids.length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">None.</div>}
        {voids.map((v, i) => (
          <div key={i} className="flex items-center justify-between py-0.5" data-testid={`pos-void-audit-invoice-${v.voided_invoice}`}>
            <span className="tabular-nums">{v.voided_invoice} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{v.reason_code}</span></span>
            <span className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">by {v.authorised_by} · {new Date(v.at).toLocaleTimeString()}</span>
          </div>
        ))}
      </ReportSection>
      <ReportSection title="Discounts">
        {discountedOrders.length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">None.</div>}
        {discountedOrders.map((o) => {
          const t = computeTotalsFor(state, o.order.id);
          return (
            <div key={o.order.id} className="flex items-center justify-between py-0.5" data-testid={`pos-void-audit-disc-${o.order.id}`}>
              <span className="tabular-nums">{o.order.invoice_no || o.order.id} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{o.order.discount.reason}</span></span>
              <span className="tabular-nums">−₹{fmt2(t.discount)} · by {o.order.discount.authorised_by}</span>
            </div>
          );
        })}
      </ReportSection>
      <ReportSection title="Ticket cancellations">
        {cancels.length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">None.</div>}
        {cancels.map((c, i) => (
          <div key={i} className="flex items-center justify-between py-0.5" data-testid={`pos-void-audit-cancel-${i}`}>
            <span className="tabular-nums">{c.ticket_id} · {c.item_name} × {c.qty_cancelled} · <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">{c.reason_code}</span></span>
            <span className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">by {c.authorised_by}</span>
          </div>
        ))}
      </ReportSection>
    </ReportShell>
  );
}

export function ItemSalesReport({ state, onClose }) {
  const sales = useMemo(() => aggregateSales(state), [state]);
  const groups = {};
  for (const [id, row] of Object.entries(sales.by_item)) {
    const cat = row.category || "cat_open";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push({ id, ...row });
  }
  const totalValue = Object.values(sales.by_item).reduce((s, r) => s + r.value, 0);
  return (
    <ReportShell title="Item sales" subtitle="Quantity + value by item, grouped by category, with contribution %."
      onClose={onClose} testid="pos-report-item-sales">
      {Object.entries(groups).length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No sales yet.</div>}
      {Object.entries(groups).map(([cat, rows]) => {
        const catMeta = CATEGORIES.find((c) => c.id === cat) || { name: cat === "cat_open" ? "Open item" : cat, icon: "•" };
        return (
          <ReportSection key={cat} title={`${catMeta.icon} ${catMeta.name}`}>
            <table className="w-full text-[11px]" data-testid={`pos-item-sales-cat-${cat}`}>
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  <th className="text-left py-1 pr-2">Item</th>
                  <th className="text-right py-1 pr-2">Qty</th>
                  <th className="text-right py-1 pr-2">Value</th>
                  <th className="text-right py-1">%</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#EEF0EC]">
                    <td className="py-0.5 pr-2">{r.name}{r.open_item && <span className="ml-1 text-[9px] text-emerald-800">· open</span>}</td>
                    <td className="py-0.5 pr-2 text-right tabular-nums">{r.qty}</td>
                    <td className="py-0.5 pr-2 text-right tabular-nums">₹{fmt2(r.value)}</td>
                    <td className="py-0.5 text-right tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{totalValue > 0 ? ((r.value / totalValue) * 100).toFixed(1) : "0.0"}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportSection>
        );
      })}
    </ReportShell>
  );
}

export function HourlySalesReport({ state, onClose }) {
  const sales = useMemo(() => aggregateSales(state), [state]);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <ReportShell title="Hourly sales" subtitle="Sales and covers by hour."
      onClose={onClose} testid="pos-report-hourly">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            <th className="text-left py-1.5">Hour</th>
            <th className="text-right py-1.5">Bills</th>
            <th className="text-right py-1.5">Covers</th>
            <th className="text-right py-1.5">Sales</th>
          </tr>
        </thead>
        <tbody>
          {hours.map((h) => {
            const row = sales.by_hour[h];
            if (!row) return null;
            return (
              <tr key={h} className="border-b border-[#EEF0EC]" data-testid={`pos-hourly-row-${h}`}>
                <td className="py-1 tabular-nums">{String(h).padStart(2, "0")}:00</td>
                <td className="py-1 text-right tabular-nums">{row.bills}</td>
                <td className="py-1 text-right tabular-nums">{row.covers}</td>
                <td className="py-1 text-right tabular-nums">₹{fmt2(row.sales)}</td>
              </tr>
            );
          })}
          {Object.keys(sales.by_hour).length === 0 && (
            <tr><td colSpan={4} className="py-4 italic text-center text-[color:var(--pos-color-secondary-text,#5C6B64)]">No sales yet.</td></tr>
          )}
        </tbody>
      </table>
    </ReportShell>
  );
}

export function TaxSummaryReport({ state, onClose }) {
  const sales = useMemo(() => aggregateSales(state), [state]);
  return (
    <ReportShell title="Tax summary" subtitle="Taxable value + tax by rate and regime, in the shape a return needs."
      onClose={onClose} testid="pos-report-tax">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            <th className="text-left py-1.5">Regime</th>
            <th className="text-left py-1.5">Rate</th>
            <th className="text-right py-1.5">Taxable</th>
            <th className="text-right py-1.5">Tax</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-[#EEF0EC]" data-testid="pos-tax-cgst">
            <td>CGST (Food)</td><td className="tabular-nums">2.5%</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_cgst / 0.025)}</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_cgst)}</td>
          </tr>
          <tr className="border-b border-[#EEF0EC]" data-testid="pos-tax-sgst">
            <td>SGST (Food)</td><td className="tabular-nums">2.5%</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_sgst / 0.025)}</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_sgst)}</td>
          </tr>
          <tr className="border-b border-[#EEF0EC]" data-testid="pos-tax-vat">
            <td>VAT (Liquor)</td><td className="tabular-nums">20%</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_vat / 0.20)}</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_vat)}</td>
          </tr>
          <tr className="border-t border-[color:var(--pos-color-border,#E2E8DF)] font-semibold">
            <td colSpan={3}>Tax total</td>
            <td className="text-right tabular-nums">₹{fmt2(sales.tax_total)}</td>
          </tr>
        </tbody>
      </table>
    </ReportShell>
  );
}

export function SettlementSummaryReport({ state, onClose }) {
  const stl = useMemo(() => aggregateSettlements(state), [state]);
  return (
    <ReportShell title="Settlement summary" subtitle="By mode, with references listed for card, UPI, aggregator, and due."
      onClose={onClose} testid="pos-report-settlement">
      {Object.entries(stl.by_mode).length === 0 && <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No settlements yet.</div>}
      {Object.entries(stl.by_mode).map(([mode, row]) => (
        <ReportSection key={mode} title={mode.replace(/_/g, " ")}>
          <ReportRow label="Count" value={<span className="tabular-nums">{row.count}</span>} testid={`pos-settle-${mode}-count`} />
          <ReportRow label="Total" value={`₹${fmt2(row.total)}`} testid={`pos-settle-${mode}-total`} bold />
          {row.refs.length > 0 && (
            <div className="text-[11px] mt-1 pl-2">
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">References</span>
              <div className="mt-0.5 font-mono text-[10px] tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)] break-words">{row.refs.join(", ")}</div>
            </div>
          )}
        </ReportSection>
      ))}
    </ReportShell>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 10 · Ticket slip preview (thermal render)
// ────────────────────────────────────────────────────────────────────
// Sprint 2 correction Part 5 · This is a RECORD, not an approval.
// The ticket has already fired by the time this dialog appears. The
// only actions available are (a) close the preview and (b) reprint
// the slip — the physical thermal printer sometimes fails and the
// kitchen needs a fresh copy. Nothing here can un-fire the ticket.
// Sprint 2 correction Part 3 · The steward line reads the resolved
// STAFF NAME embedded in the slip payload, never the raw UUID.
// ═══════════════════════════════════════════════════════════════════
export function TicketSlipDialog({ slip, onClose, onReprint }) {
  if (!slip) return null;
  const isBar = slip.dest === "bar";
  const isCancel = slip.kind === "cancel";
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[340px] p-0 gap-0 border border-slate-300 overflow-hidden"
        data-testid="pos-ticket-slip"
        data-slip-dest={slip.dest}
        data-slip-kind={slip.kind || "fire"}>
        {/* Sprint 2 correction · Part 5. Label the header so nobody
            reads this dialog as "approve firing the ticket". The
            ticket has already gone to the kitchen. */}
        <div className={`px-3 py-1.5 text-center text-[9px] uppercase tracking-widest font-semibold border-b
          ${isCancel ? "bg-rose-50 border-rose-200 text-rose-800"
                     : "bg-[color:var(--pos-color-page-bg,#F4F6F2)] border-[color:var(--pos-color-border,#E2E8DF)] text-[color:var(--pos-color-secondary-text,#5C6B64)]"}`}
          data-testid="pos-ticket-slip-banner">
          {isCancel ? "Cancellation slip · already sent" : "Slip · record of what was sent"}
        </div>
        <div className={`px-3 py-2 border-b space-y-0 text-center
          ${isCancel ? "bg-rose-100 border-rose-300" : (isBar ? "bg-orange-100 border-orange-300" : "bg-sky-100 border-sky-300")}`}>
          <div className="text-[13px] font-mono font-bold tracking-widest text-[color:var(--pos-color-primary-text,#1A2421)]">
            {isCancel ? "*** CANCELLED ***" : (isBar ? "*** BAR ***" : "*** KITCHEN ***")}
          </div>
          <div className="text-[10px] font-mono tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">
            {slip.ticket_id} · {new Date(slip.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        <div className="p-3 bg-white font-mono text-[12px] leading-[1.35] text-[color:var(--pos-color-primary-text,#1A2421)]">
          <div className="text-center mb-1">
            <div className="tabular-nums">{slip.type || "DINE-IN"}{slip.covers ? ` · ${slip.covers} PAX` : ""}</div>
            {/* Part 8.2 (Sprint 4) · table/token bold + larger. The
                kitchen reads this first; on a thermal printer bold
                survives (colour does not). */}
            <div className="tabular-nums text-[22px] font-extrabold tracking-wide" data-testid="pos-ticket-slip-table">
              {slip.table_label || slip.token || "—"}
            </div>
          </div>
          <div className="border-t border-b border-dashed border-slate-400 py-0.5 tabular-nums text-center"
               data-testid="pos-ticket-slip-steward">
            STEWARD: {slip.steward_name || "—"}
          </div>
          <div className="mt-2 space-y-0.5" data-testid="pos-ticket-slip-items">
            {(slip.lines || []).map((l, i) => (
              <div key={i} className="grid grid-cols-[3ch_1fr] gap-2">
                <div className="text-right tabular-nums font-bold">{l.qty}</div>
                <div>
                  <div>{l.item_name}</div>
                  {l.note && (
                    // Part 8.2 (Sprint 4) · notes are BOLD as well as red.
                    // Colour alone is insufficient on a thermal printer;
                    // bold survives, red does not.
                    <div className="text-rose-700 pl-0 uppercase text-[12px] font-extrabold tracking-wide" data-testid={`pos-ticket-slip-note-${i}`}>
                      ** {l.note} **
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isCancel && slip.authorised_by && (
            <div className="mt-2 pt-1 border-t border-dashed border-slate-400 text-[10px] tabular-nums text-center">
              AUTH: {slip.authorised_by}
              {slip.reason_code && <div>REASON: {slip.reason_code}</div>}
            </div>
          )}
          <div className="mt-2 text-center text-[9px] tracking-widest">
            {isCancel ? "PULL FROM PASS" : "-- END OF TICKET --"}
          </div>
        </div>
        <div className="p-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          {/* Sprint 2 correction Part 5 · Reprint action for thermal
              printer failures. Close no longer says "Acknowledge" —
              that language implied approval of an already-fired ticket. */}
          {!isCancel && onReprint && (
            <button type="button" onClick={() => onReprint(slip)}
              data-testid="pos-ticket-slip-reprint"
              className="h-9 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[color:var(--pos-color-primary-text,#1A2421)] text-[11px] font-semibold hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
              Reprint
            </button>
          )}
          <button type="button" onClick={onClose}
            data-testid="pos-ticket-slip-close"
            className="h-9 px-3 rounded bg-[color:var(--color-primary)] text-white text-[11px] font-semibold hover:bg-[color:var(--color-primary-hover)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 11 · Kitchen display (KDS)
// ═══════════════════════════════════════════════════════════════════
export function KitchenDisplay({ state, dispatch, onClose }) {
  const [filter, setFilter] = useState("all"); // all | kitchen | bar
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // Group non-terminal orders' fired lines by ticket_id.
  const tickets = useMemo(() => {
    const byTicket = new Map();
    for (const slice of Object.values(state.orders)) {
      if (slice.order.state === ORDER_STATE.SETTLED) continue;
      for (const l of slice.lines || []) {
        if (!l.ticket_id) continue;
        if (!byTicket.has(l.ticket_id)) byTicket.set(l.ticket_id, {
          ticket_id: l.ticket_id,
          dest: l.ticket_dest || (String(l.ticket_id).startsWith("BOT") ? "bar" : "kitchen"),
          fired_at: l.ticket_fired_at || 0,
          type: slice.order.type,
          table_label: TABLES.find((t) => t.id === slice.order.table_id)?.label,
          token: slice.order.token,
          steward_id: slice.order.steward_id,
          lines: [],
          ready_at: null,
          fully_ready: true,
        });
        const grp = byTicket.get(l.ticket_id);
        grp.lines.push(l);
        if (!l.ticket_ready_at) grp.fully_ready = false;
        else if (!grp.ready_at || l.ticket_ready_at > grp.ready_at) grp.ready_at = l.ticket_ready_at;
      }
    }
    return Array.from(byTicket.values())
      .filter((g) => filter === "all" || g.dest === filter)
      // Firing order, oldest first.
      .sort((a, b) => a.fired_at - b.fired_at)
      // Ready tickets clear after ready_clear_ms.
      .filter((g) => !(g.fully_ready && g.ready_at && (now - g.ready_at) > POS_CONFIG.kds_ready_clear_ms));
  }, [state.orders, filter, now]);

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] p-0 gap-0 border-2 border-slate-900 bg-slate-900 text-white overflow-hidden flex flex-col"
        data-testid="pos-kitchen-display">
        <DialogHeader className="px-4 py-2 border-b border-slate-700 bg-slate-800 space-y-0 shrink-0 flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-[16px] font-semibold text-white flex items-center gap-2">
              <Utensils className="w-5 h-5" /> Kitchen Display · {tickets.length} live
            </DialogTitle>
            <DialogDescription className="text-[11px] text-slate-300">
              Oldest first. Tap an item to mark prepared. Elapsed escalates at {POS_CONFIG.kds_escalation_ms[0]/60000}m and {POS_CONFIG.kds_escalation_ms[1]/60000}m.
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" data-testid="pos-kds-filter">
              {[["all", "All"], ["kitchen", "Kitchen"], ["bar", "Bar"]].map(([f, l]) => (
                <button key={f} type="button" onClick={() => setFilter(f)}
                  data-testid={`pos-kds-filter-${f}`}
                  className={`h-8 px-3 rounded text-[12px] font-semibold ${filter === f ? "bg-white text-slate-900" : "bg-slate-700 text-white hover:bg-slate-600"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button type="button" onClick={onClose}
              data-testid="pos-kds-close"
              className="h-8 px-3 rounded bg-rose-700 text-white text-[12px] font-semibold hover:bg-rose-800">Close</button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-4 gap-3">
          {tickets.length === 0 && (
            <div className="col-span-4 text-center text-slate-400 py-16 text-[16px] italic">
              No live tickets.
            </div>
          )}
          {tickets.map((g) => (
            <KDSCard key={g.ticket_id} group={g} now={now}
              onMarkPrepared={(line_id) => {/* prep only tracked on ticket for prototype */}}
              onMarkTicketReady={() => dispatch({ type: ACT.TICKET_MARKED_READY, payload: { ticket_id: g.ticket_id } })} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KDSCard({ group, now, onMarkTicketReady }) {
  const elapsed = now - (group.fired_at || now);
  const [warnMs, urgentMs] = POS_CONFIG.kds_escalation_ms;
  const level = elapsed > urgentMs ? "urgent"
              : elapsed > warnMs   ? "warn"
              : "fresh";
  const isBar = group.dest === "bar";
  const cls = level === "urgent" ? "bg-rose-600 border-rose-300"
            : level === "warn"   ? "bg-amber-500 border-amber-300"
            : (isBar ? "bg-orange-700 border-orange-300" : "bg-sky-700 border-sky-300");
  const elapsedMin = Math.floor(elapsed / 60000);
  return (
    <div className={`rounded-lg border-2 shadow p-3 flex flex-col ${cls}`}
      data-testid={`pos-kds-card-${group.ticket_id}`}
      data-elapsed-level={level}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[18px] font-bold tabular-nums">{group.ticket_id}</div>
        <div className="text-[24px] font-bold tabular-nums" data-testid={`pos-kds-card-${group.ticket_id}-elapsed`}>
          {elapsedMin}m
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-wider opacity-90">
        {group.type.replace("_", " ")} · {group.table_label || group.token || "—"}
      </div>
      <div className="flex-1 mt-2 space-y-1 text-[15px]">
        {group.lines.map((l, i) => (
          <div key={i} className={`flex items-center gap-2 ${l.ticket_ready_at ? "opacity-60" : ""}`}
            data-testid={`pos-kds-card-${group.ticket_id}-line-${i}`}>
            <span className="w-6 text-right font-bold tabular-nums">{l.qty}</span>
            <span className={`flex-1 ${l.is_cancelled ? "line-through" : ""}`}>{l.item_name}</span>
          </div>
        ))}
        {group.lines.some((l) => l.note) && (
          <div className="mt-1 text-[13px] font-bold text-yellow-200 uppercase" data-testid={`pos-kds-card-${group.ticket_id}-note`}>
            {group.lines.filter((l) => l.note).map((l) => l.note).join(" · ")}
          </div>
        )}
      </div>
      {group.fully_ready
        ? (
          <div className="mt-2 py-1 rounded bg-emerald-700 border border-emerald-300 text-center text-[13px] font-semibold"
            data-testid={`pos-kds-card-${group.ticket_id}-ready`}>
            READY
          </div>
        )
        : (
          <button type="button" onClick={onMarkTicketReady}
            data-testid={`pos-kds-card-${group.ticket_id}-mark-ready`}
            className="mt-2 py-1.5 rounded bg-white/20 hover:bg-white/30 border border-white/40 text-[13px] font-semibold">
            Mark all ready
          </button>
        )
      }
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 12 · QSR token queue
// ═══════════════════════════════════════════════════════════════════
export function TokenQueue({ state, dispatch, onClose, guestFacing = false }) {
  const tokens = state.session.tokens || [];
  const preparing = tokens.filter((t) => t.state === "preparing");
  const ready     = tokens.filter((t) => t.state === "ready");
  const collected = tokens.filter((t) => t.state === "collected");
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className={`${guestFacing ? "max-w-[95vw] w-[95vw] max-h-[95vh] bg-black text-white border-2 border-white" : "max-w-[840px] w-[92vw] max-h-[92vh] border border-[color:var(--pos-color-border,#E2E8DF)]"}
        p-0 gap-0 overflow-hidden flex flex-col`}
        data-testid={guestFacing ? "pos-token-guest-display" : "pos-token-queue"}>
        {!guestFacing && (
          <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] space-y-0 flex-row items-center justify-between shrink-0">
            <div>
              <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Token queue</DialogTitle>
              <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                Tap a token in Preparing to mark it Ready. Tap a Ready token to mark it collected.
                KDS bumps promote automatically.
              </DialogDescription>
            </div>
            <button type="button" onClick={onClose}
              data-testid="pos-token-queue-close"
              className="h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px]">Close</button>
          </DialogHeader>
        )}
        {guestFacing && (
          <div className="px-5 py-3 border-b border-white/20 flex items-center justify-between">
            <div className="text-[18px] font-semibold tracking-wider">TOKEN STATUS</div>
            <button type="button" onClick={onClose}
              data-testid="pos-token-guest-close"
              className="h-8 px-3 rounded border border-white/30 text-[11px]">Close</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-4">
          <TokenColumn title="Preparing" tokens={preparing} kind="preparing" guestFacing={guestFacing}
            orders={state.orders}
            onMark={(n) => dispatch({ type: ACT.TOKEN_MARKED_READY, payload: { number: n } })} />
          <TokenColumn title="Ready" tokens={ready} kind="ready" guestFacing={guestFacing}
            orders={state.orders}
            onMark={(n) => dispatch({ type: ACT.TOKEN_MARKED_COLLECTED, payload: { number: n } })} />
        </div>
        {!guestFacing && collected.length > 0 && (
          <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-4 py-2 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            Collected today · <span className="tabular-nums">{collected.length}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Sprint 5 correction · Token card. Guest-facing view is intentionally
// spartan: token number + first name only, HUGE type, no prices, no
// items, no phone, no full name, no aggregator refs. Cashier view
// carries the operational context: full guest name, order type badge,
// aggregator platform ref, elapsed time.
function TokenCard({ token, kind, onMark, guestFacing, orderGuestName }) {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const start = token.allocated_at || Date.now();
  const elapsedMin = Math.max(0, Math.floor((Date.now() - start + tick * 0) / 60000));
  // Sprint 5 correction · guest name may have been captured AFTER
  // the token was allocated (guest lookup by phone happens once the
  // takeaway dialog is saved). Prefer the current order snapshot if
  // it has a name, fall back to what was stamped on the token.
  const guestName = orderGuestName || token.guest_name || "";
  const firstName = guestName.trim().split(/\s+/)[0] || "";
  const platform = token.order_type === "SWIGGY" ? "Swiggy"
                : token.order_type === "ZOMATO"  ? "Zomato"
                : "Takeaway";
  const isAggregator = token.order_type === "SWIGGY" || token.order_type === "ZOMATO";

  if (guestFacing) {
    return (
      <div
        data-testid={`pos-token-${token.number}`}
        data-token-state={token.state}
        className="aspect-square flex flex-col items-center justify-center bg-white/10 rounded p-3 min-h-[180px]">
        <div className="text-[72px] leading-none font-bold tabular-nums tracking-tight">
          {token.label || `T-${token.number}`}
        </div>
        {firstName && (
          <div className="mt-2 text-[24px] font-medium truncate max-w-full">{firstName}</div>
        )}
      </div>
    );
  }

  // Cashier card — richer surface.
  const canTap = (kind === "preparing" || kind === "ready") && !!onMark;
  return (
    <button
      type="button"
      onClick={() => canTap && onMark(token.number)}
      disabled={!canTap}
      data-testid={`pos-token-${token.number}`}
      data-token-state={token.state}
      data-token-platform={token.order_type || "TAKEAWAY"}
      className={`rounded border p-3 text-left flex flex-col gap-1 min-h-[132px] transition
        ${isAggregator
          ? "border-orange-300 bg-orange-50 hover:bg-orange-100"
          : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}
        ${canTap ? "cursor-pointer active:scale-[0.99]" : "cursor-default"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[28px] font-bold tabular-nums leading-none">
          {token.label || `T-${token.number}`}
        </div>
        <span
          className={`text-[9px] uppercase tracking-widest font-semibold rounded px-1.5 py-[2px] border
            ${isAggregator
              ? "text-orange-900 bg-orange-100 border-orange-300"
              : "text-slate-700 bg-slate-100 border-slate-200"}`}
          data-testid={`pos-token-${token.number}-type`}>
          {platform}
        </span>
      </div>
      {firstName && (
        <div className="text-[12px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] truncate"
          data-testid={`pos-token-${token.number}-guest`}>
          {guestName}
        </div>
      )}
      {isAggregator && token.aggregator_ref && (
        <div className="text-[10px] text-orange-900 tabular-nums truncate"
          data-testid={`pos-token-${token.number}-ref`}>
          Ref · {token.aggregator_ref}
        </div>
      )}
      <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums mt-auto"
        data-testid={`pos-token-${token.number}-elapsed`}>
        {elapsedMin === 0 ? "just now" : `${elapsedMin}m ago`}
      </div>
      {canTap && (
        <div className="text-[10px] text-emerald-800 font-medium">
          Tap to mark {kind === "preparing" ? "ready" : "collected"}
        </div>
      )}
    </button>
  );
}

function TokenColumn({ title, tokens, kind, onMark, guestFacing, orders = {} }) {
  const bg = kind === "preparing"
    ? (guestFacing ? "bg-slate-800 border-slate-700" : "bg-amber-50 border-amber-200")
    : (guestFacing ? "bg-emerald-800 border-emerald-500" : "bg-emerald-50 border-emerald-200");
  const label = kind === "preparing"
    ? (guestFacing ? "text-slate-300" : "text-amber-800")
    : (guestFacing ? "text-emerald-200" : "text-emerald-800");
  return (
    <div className={`rounded-lg border-2 p-3 ${bg}`} data-testid={`pos-token-column-${kind}`}>
      <div className={`text-center ${guestFacing ? "text-[28px]" : "text-[14px]"} uppercase tracking-widest font-semibold ${label}`}>
        {guestFacing ? title.toUpperCase() : title}
      </div>
      <div className={`mt-3 grid ${guestFacing ? "grid-cols-3 gap-4" : "grid-cols-3 gap-2"}`}>
        {tokens.length === 0 && (
          <div className={`${guestFacing ? "col-span-3" : "col-span-3"} text-center italic ${guestFacing ? "text-white/60 text-[16px]" : "text-[color:var(--pos-color-secondary-text,#5C6B64)] text-[11px]"} py-6`}>
            {kind === "preparing" ? "No tokens preparing." : "No tokens ready."}
          </div>
        )}
        {tokens.map((t) => (
          <TokenCard key={t.number}
            token={t} kind={kind} onMark={onMark} guestFacing={guestFacing}
            orderGuestName={orders[t.order_id]?.order?.guest_name} />
        ))}
      </div>
    </div>
  );
}
